import { supabase } from "../../lib/supabase";
import { WebRTCService } from "../../services/webrtc";

export type SignalKind =
  | "invite"
  | "ringing"
  | "offer"
  | "answer"
  | "ice"
  | "end"
  | "decline"
  | "busy";
export type CallSignal = {
  id: string;
  call_id: string;
  sender_id: string;
  recipient_id: string;
  signal_type: SignalKind;
  payload: Record<string, unknown>;
};

export class CallService {
  private rtc: WebRTCService | null = null;
  private callId: string | null = null;
  constructor(
    private readonly me: string,
    private readonly remote: string,
    private readonly onRemote: (stream: MediaStream) => void,
  ) {}
  async start(conversationId: string, video: boolean) {
    const { data, error } = await supabase.rpc("create_private_call", {
      cid: conversationId,
      recipient: this.remote,
      kind: video ? "video" : "audio",
    });
    if (error) throw error;
    this.callId = data as string;
    this.rtc = new WebRTCService(
      this.onRemote,
      (candidate) =>
        void this.signal("ice", candidate.toJSON() as Record<string, unknown>),
    );
    const stream = await this.rtc.start(video);
    await this.signal("invite", { video });
    const offer = await this.rtc.offer();
    await this.signal("offer", offer as unknown as Record<string, unknown>);
    return { callId: this.callId, stream };
  }
  async accept(
    callId: string,
    video: boolean,
    offer: RTCSessionDescriptionInit,
  ) {
    this.callId = callId;
    this.rtc = new WebRTCService(
      this.onRemote,
      (candidate) =>
        void this.signal("ice", candidate.toJSON() as Record<string, unknown>),
    );
    const stream = await this.rtc.start(video);
    const answer = await this.rtc.answer(offer);
    await supabase.rpc("set_call_status", {
      call_id: callId,
      next_status: "accepted",
    });
    await this.signal("answer", answer as unknown as Record<string, unknown>);
    return stream;
  }
  async receive(signal: CallSignal) {
    if (!this.rtc) return;
    if (signal.signal_type === "answer")
      await this.rtc.receiveAnswer(
        signal.payload as unknown as RTCSessionDescriptionInit,
      );
    if (signal.signal_type === "ice")
      await this.rtc.addIce(signal.payload as RTCIceCandidateInit);
  }
  async end(status: "ended" | "declined" | "busy" = "ended") {
    if (this.callId) {
      await this.signal(
        status === "ended" ? "end" : status === "declined" ? "decline" : "busy",
        {},
      );
      await supabase.rpc("set_call_status", {
        call_id: this.callId,
        next_status: status,
      });
    }
    this.rtc?.cleanup();
    this.rtc = null;
    this.callId = null;
  }
  private async signal(type: SignalKind, payload: Record<string, unknown>) {
    if (!this.callId) throw new Error("No active call");
    const { error } = await supabase
      .from("call_signals")
      .insert({
        call_id: this.callId,
        sender_id: this.me,
        recipient_id: this.remote,
        signal_type: type,
        payload,
      });
    if (error) throw error;
  }
}
